import { addDays, differenceInCalendarDays, format, parseISO } from "date-fns";
import type {
  AnalyticsAggregateRequest,
  AnalyticsAggregateRow,
  AnalyticsBucket,
  AnalyticsFieldKey,
} from "@adlywise/shared";
import {
  BUCKETS,
  dimensionColumns,
  effectiveSortBy,
  FIELD_LABELS,
  SOURCES,
  metricGroupsFor,
  type AnalyticsConfig,
  type MetricGroup,
} from "@/features/analytics/components/ConfigurePanel";
import type { NameMaps } from "@/features/analytics/hooks/useAnalyticsNameMaps";

// What a finished run was, in words, and its results as plain cells: the run
// summary above the table and the xlsx export both read from here, so the
// two cannot describe the same run differently. Everything comes from the
// SUBMITTED run (its config + request body), never the panel's live settings.

const DATE_FMT = "yyyy-MM-dd";

// The bucket string is Kyiv wall-clock "YYYY-MM-DDThh:mm:ss". We format from the
// DATE part only (parsed as a local date) so there is no TZ drift, then present
// per granularity:
//   hour  → "YYYY-MM-DD HH:00"
//   day   → "YYYY-MM-DD"
//   week  → "<Mon> – <Sun>" (start .. start+6)
//   month → "YYYY-MM"
export const formatBucket = (
  bucket: string,
  granularity: AnalyticsBucket,
): string => {
  const datePart = bucket.slice(0, 10);
  const start = parseISO(datePart);
  if (Number.isNaN(start.getTime())) return bucket;

  switch (granularity) {
    case "hour": {
      const hh = bucket.slice(11, 13) || "00";
      return `${datePart} ${hh}:00`;
    }
    case "day":
      return datePart;
    case "week":
      return `${format(start, DATE_FMT)} – ${format(addDays(start, 6), DATE_FMT)}`;
    case "month":
      return format(start, "yyyy-MM");
    default:
      return datePart;
  }
};

// A calendar day out of a request instant ("2026-10-26T12:00:00.000Z" →
// "2026-10-26"): the request carries the picked day as noon UTC (see
// toRangeInstants), so its date part IS the day, in any browser timezone.
const dayOf = (instant: string): string => instant.slice(0, 10);

// "1–26 Oct 2026 · 26 days": whole days, both ends included, which the day
// count makes plain without saying so. Takes YYYY-MM-DD days.
export const formatPeriod = (from: string, to: string): string => {
  const a = parseISO(from);
  const b = parseISO(to);
  const days = differenceInCalendarDays(b, a) + 1;
  const count = `${days.toString()} ${days === 1 ? "day" : "days"}`;
  const range =
    from === to
      ? format(b, "d MMM yyyy")
      : format(a, "yyyy-MM") === format(b, "yyyy-MM")
        ? `${format(a, "d")}–${format(b, "d MMM yyyy")}`
        : format(a, "yyyy") === format(b, "yyyy")
          ? `${format(a, "d MMM")} – ${format(b, "d MMM yyyy")}`
          : `${format(a, "d MMM yyyy")} – ${format(b, "d MMM yyyy")}`;
  return `${range} · ${count}`;
};

const toArray = (value: unknown): unknown[] =>
  Array.isArray(value)
    ? value
    : value === null || value === undefined
      ? []
      : [value];

const nameOf = (names: NameMaps, key: AnalyticsFieldKey, id: unknown): string =>
  names[key]?.[String(id)] ?? String(id);

export type RunFilter = { label: string; value: string };

export type RunDescription = {
  from: string;
  // Inclusive: the report covers all of this day.
  to: string;
  granularity: string;
  source: string;
  metrics: string[];
  groupBy: string[];
  sortBy: string[];
  filters: RunFilter[];
};

export const describeRun = (
  config: AnalyticsConfig,
  request: AnalyticsAggregateRequest,
  names: NameMaps,
): RunDescription => ({
  from: dayOf(request.range.from),
  to: dayOf(request.range.to),
  granularity:
    BUCKETS.find((b) => b.value === config.bucket)?.label ?? config.bucket,
  source:
    SOURCES.find((s) => s.value === config.source)?.label ?? config.source,
  metrics: metricGroupsFor(config).map((group) => group.label),
  groupBy: config.groupBy.map((key) => FIELD_LABELS[key]),
  sortBy: effectiveSortBy(config).map((key) =>
    key === "bucket" ? "Time" : FIELD_LABELS[key],
  ),
  // The request's filter, so only what was sent: conditions the run actually
  // applied, values as names (never raw UUIDs/ids where a name is known).
  filters: (request.filter.all ?? []).map((condition) => {
    const key = condition.field as AnalyticsFieldKey;
    const values = toArray(condition.value)
      .map((v) => nameOf(names, key, v))
      .join(", ");
    return {
      label: FIELD_LABELS[key] ?? condition.field,
      value: condition.op === "nin" ? `not: ${values}` : values,
    };
  }),
});

// The request's filter values as pseudo-rows, so useAnalyticsNameMaps (which
// resolves the ids it finds in rows) names them along with the table's.
export const filterNameRows = (
  request: AnalyticsAggregateRequest | null,
): { rows: AnalyticsAggregateRow[]; keys: AnalyticsFieldKey[] } => {
  const conditions = request?.filter.all ?? [];
  return {
    rows: conditions.map(
      (c) =>
        ({
          bucket: "",
          [c.field]: toArray(c.value),
        }) as AnalyticsAggregateRow,
    ),
    keys: conditions.map((c) => c.field as AnalyticsFieldKey),
  };
};

// ── xlsx sheets (arrays of rows; a null cell is left blank) ─────────────────

export type Cell = string | number | null;

export type AnalyticsWorkbookSheets = {
  config: Cell[][];
  report: Cell[][];
};

// config: one "Label | value" row per setting; Grouping and Filters are a
// header row followed by one row per entry (grouping in column B, filters as
// property | values).
const configSheet = (run: RunDescription): Cell[][] => [
  ["From", run.from],
  ["To", run.to],
  ["Granularity", run.granularity],
  ["Source", run.source],
  ["Metrics", run.metrics.join(", ")],
  ["Grouping", null],
  ...run.groupBy.map((label): Cell[] => [null, label]),
  ["Sort by", run.sortBy.join(", ")],
  ["Filters", null],
  ...run.filters.map((f): Cell[] => [f.label, f.value]),
];

const metricHeaders = (group: MetricGroup): string[] => {
  if (group.kind === "pair") {
    return [`${group.label} total`, `${group.label} devices`];
  }
  if (group.format === "money") return [`${group.label} (USD)`];
  if (group.format === "percent") return [`${group.label} (%)`];
  return [group.label];
};

// A count the table shows as 0 when absent (it formats a missing count as 0);
// money and percentages are decimal strings and stay blank when null (a ratio
// with no denominator has no value).
const countCell = (value: unknown): number => Number(value ?? 0);
const decimalCell = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

// report: the on-screen table flattened to one header row — Bucket among the
// group-by columns (where the table shows it), then the metric columns — with numbers as numbers and
// dimension ids as the names the table shows.
const reportSheet = (
  config: AnalyticsConfig,
  rows: AnalyticsAggregateRow[],
  names: NameMaps,
): Cell[][] => {
  const groups = metricGroupsFor(config);
  const header: Cell[] = [
    ...dimensionColumns(config).map((key) =>
      key === "bucket" ? "Bucket" : FIELD_LABELS[key],
    ),
    ...groups.flatMap(metricHeaders),
  ];
  const body = rows.map((row): Cell[] => [
    ...dimensionColumns(config).map((key) => {
      if (key === "bucket") return formatBucket(row.bucket, config.bucket);
      const raw = row[key];
      if (raw === null || raw === undefined) return null;
      return toArray(raw)
        .map((v) => nameOf(names, key, v))
        .join(", ");
    }),
    ...groups.flatMap((group): Cell[] =>
      group.kind === "pair"
        ? [
            countCell(row[`${group.key}_total`]),
            countCell(row[`${group.key}_devices`]),
          ]
        : [
            group.format === "count"
              ? countCell(row[group.key])
              : decimalCell(row[group.key]),
          ],
    ),
  ]);
  return [header, ...body];
};

export const buildAnalyticsWorkbookSheets = ({
  config,
  request,
  rows,
  names,
}: {
  config: AnalyticsConfig;
  request: AnalyticsAggregateRequest;
  rows: AnalyticsAggregateRow[];
  names: NameMaps;
}): AnalyticsWorkbookSheets => ({
  config: configSheet(describeRun(config, request, names)),
  report: reportSheet(config, rows, names),
});

export const workbookFileName = (request: AnalyticsAggregateRequest): string =>
  `analytics_${dayOf(request.range.from)}_${dayOf(request.range.to)}.xlsx`;
